const SCRIPT_LOADED = new Set<string>();

export function getFormsDomain(env: string): string {
  return env === 'qa' ? 'hsformsqa.net' : 'hsforms.net';
}

// The HubSpot forms embed script (forms/embed/v3.js) is a loader that
// bootstraps the forms SDK asynchronously. On some environments the
// script element's `load` event never fires even though the SDK is
// successfully initialised on `window.HubSpotForms`. This helper polls
// for HubSpotForms as a reliable fallback signal that the script has
// finished.
function waitForHubSpotForms(onReady: () => void): () => void {
  // @ts-ignore HubSpotForms is injected by the embed script
  if (window.HubSpotForms) {
    onReady();
    return () => {};
  }
  const id = setInterval(() => {
    // @ts-ignore HubSpotForms is injected by the embed script
    if (window.HubSpotForms) {
      clearInterval(id);
      onReady();
    }
  }, 100);
  return () => clearInterval(id);
}

export function injectScript(
  src: string,
  onLoad?: () => void,
  onError?: (error: Event) => void
): void {
  // Script was previously loaded — call onLoad even if the element was removed
  if (SCRIPT_LOADED.has(src)) {
    onLoad?.();
    return;
  }

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${src}"]`
  );
  if (existing) {
    // The script may have been injected externally (e.g. by the CMS page)
    // or appended by a previous mount cycle (React StrictMode). Either way
    // the load event may have already fired or may never fire for this
    // embed script. Use waitForHbspt as the reliable completion signal.
    let called = false;
    const handleLoad = () => {
      if (called) return;
      called = true;
      SCRIPT_LOADED.add(src);
      onLoad?.();
    };

    existing.addEventListener('load', handleLoad, { once: true });
    existing.addEventListener(
      'error',
      (e) => {
        existing.remove();
        onError?.(e);
      },
      { once: true }
    );

    waitForHubSpotForms(handleLoad);
    return;
  }

  const script = document.createElement('script');
  script.src = src;
  script.type = 'text/javascript';

  let called = false;
  const handleLoad = () => {
    if (called) return;
    called = true;
    SCRIPT_LOADED.add(src);
    onLoad?.();
  };

  script.addEventListener('load', handleLoad, { once: true });
  script.addEventListener(
    'error',
    (e) => {
      // Remove the failed script so a retry can re-insert it
      script.remove();
      onError?.(e);
    },
    { once: true }
  );
  document.body.appendChild(script);

  // The embed script's load event may not fire reliably on all
  // environments. Poll for HubSpotForms as a fallback.
  waitForHubSpotForms(handleLoad);
}
